import { ChangeEvent, FC, useEffect } from 'react';

import {
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Typography,
} from 'antd';
import { FormInstance } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import RTooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { PlantForm } from '@/types/plant';
import {
  formatInputNumber,
  normalizePositiveIntegerValuesInput,
  normalizePositiveNumericValuesInput,
  removeCommas,
} from '@/utils';
const { Option } = Select;

type Props = {
  disabled?: boolean;
  form: FormInstance<PlantForm>;
  initialValues?: PlantForm;
};

const FinancialMetrics: FC<Props> = ({
  disabled = false,
  form,
  initialValues,
}) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const theme = useTheme();
  const initialYearCount = form.getFieldValue('investmentLag');
  const initialPreopCapexDistribution = form.getFieldValue(
    'preopCapexDistribution'
  );
  const yearCount = Form.useWatch('investmentLag', form) || initialYearCount;
  const preopCapexDistribution =
    Form.useWatch('preopCapexDistribution', form) ||
    initialPreopCapexDistribution;

  const loanFinancing = Form.useWatch('loanFinancing', form) || 0;
  const loanRepaymentPeriod = Form.useWatch('loanRepaymentPeriod', form);

  const handleLoanFinancingChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    form.setFieldValue('loanFinancing', value);

    if (value === 0) {
      form.setFieldsValue({
        loanRepaymentPeriod: 1,
        yearlyInterestExpense: [0],
        loanFundedCapexRatio: 0,
      });
    }
  };

  const handleLoanRepaymentPeriodChange = () => {
    const currentPeriod = form.getFieldValue('loanRepaymentPeriod');
    const prevArray = form.getFieldValue('yearlyInterestExpense') || [];
    let newArray = [];
    if (prevArray.length > currentPeriod) {
      newArray = prevArray.slice(0, currentPeriod);
    } else {
      newArray = [
        ...prevArray,
        ...Array(currentPeriod - prevArray.length).fill(0),
      ];
    }
    form.setFieldsValue({ yearlyInterestExpense: newArray });
  };

  useEffect(() => {
    if (loanRepaymentPeriod !== undefined && loanRepaymentPeriod !== null) {
      const prevArray = form.getFieldValue('yearlyInterestExpense') || [];
      let newArray = [];
      if (prevArray.length > loanRepaymentPeriod) {
        newArray = prevArray.slice(0, loanRepaymentPeriod);
      } else {
        newArray = [
          ...prevArray,
          ...Array(loanRepaymentPeriod - prevArray.length).fill(0),
        ];
      }
      form.setFieldsValue({ yearlyInterestExpense: newArray });
    }
  }, [loanRepaymentPeriod, form]);

  useEffect(() => {
    if (form) {
      if (initialValues) {
        const initialYearCount = initialValues.investmentLag;
        if (yearCount != initialYearCount) {
          const year = Number(yearCount) + 1;
          form.setFieldValue('preopCapexDistribution', Array(year).fill(''));
        }
        if (yearCount == initialYearCount) {
          form.setFieldValue(
            'preopCapexDistribution',
            initialValues.preopCapexDistribution
          );
        }
      } else {
        const year = Number(yearCount) + 1;
        if (form.isFieldTouched('investmentLag')) {
          form.setFieldValue('preopCapexDistribution', Array(year).fill(''));
        }
      }
    }
  }, [form, initialValues, yearCount]);

  // TODO: refactor this code use useWatch or formOnChange instead of useEffect
  useEffect(() => {
    if (form.isFieldTouched('investmentLag')) {
      const yearCount =
        parseInt(form.getFieldValue('investmentLag') || 0, 10) + 1;

      if (yearCount === 1) {
        form.setFieldsValue({
          preopCapexDistribution: Array(yearCount).fill(100),
        });
      }
    }
  }, [form, yearCount]);

  useEffect(() => {
    if (form && preopCapexDistribution) {
      const validateTotal = () => {
        // Calculate the total value
        const total = preopCapexDistribution.reduce(
          (sum: number, item: string) => {
            const value = parseFloat(item) || 0;
            return sum + value;
          },
          0
        );

        if (total !== 100) {
          // Set form errors if the total is not exactly 100
          form.setFields(
            preopCapexDistribution.map((_: unknown, index: number) => ({
              name: [`preopCapexDistribution`, index],
              errors: [
                total > 100
                  ? t(
                      'plants.form.financialMetrics.validation.totalPreopCapexDistribution'
                    )
                  : t(
                      'plants.form.financialMetrics.validation.totalPreopCapexDistributionExact'
                    ),
              ],
            }))
          );
        } else {
          // Clear errors if total is exactly 100
          form.setFields(
            preopCapexDistribution.map((_: unknown, index: number) => ({
              name: [`preopCapexDistribution`, index],
              errors: [],
            }))
          );
        }
      };
      validateTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preopCapexDistribution]);

  const handleNumberChange =
    (field: keyof PlantForm) => (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value.replace(/[^0-9.]/g, '');
      const formatted = formatInputNumber(rawValue);
      form?.setFieldValue(field, formatted);
    };

  const handleNumberBlur = (field: keyof PlantForm) => () => {
    const value = form?.getFieldValue(field) || '';
    form?.setFieldValue(field, removeCommas(value));
  };

  const selectAfter = (
    <Form.Item<PlantForm> style={{ margin: '0px' }} name="capexUnit">
      <Select disabled={disabled} style={{ width: 90 }}>
        <Option value="dollars">{currencySymbol}</Option>
        <Option value="dollars_per_mw">{`${currencySymbol}/MW`}</Option>
      </Select>
    </Form.Item>
  );

  return (
    <Form.Item<PlantForm>
      wrapperCol={{ span: 24 }}
      className="p-16 pb-40"
      style={{
        border: '0.5px solid',
        borderRadius: '10px',
        borderColor: theme.colors.grayLightAccent,
      }}
    >
      <Row>
        <Col className="pr-8" span={12}>
          <Form.Item<PlantForm>
            name="investmentLag"
            label={
              <Flex gap={8} align="center">
                <Typography.Text className="fs-14-regular text-black-color">
                  {t('plants.form.financialMetrics.investmentLag.label')}
                </Typography.Text>
                <RTooltip
                  title={t(
                    'plants.form.financialMetrics.investmentLag.tooltip.title'
                  )}
                  description={t(
                    'plants.form.financialMetrics.investmentLag.tooltip.description'
                  )}
                >
                  <IconInfo />
                </RTooltip>
              </Flex>
            }
            normalize={normalizePositiveIntegerValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.financialMetrics.investmentLag.requiredMessage'
                ),
              },
              {
                validator(_, value) {
                  const minValue = parseFloat(value);
                  if (isNaN(minValue) || minValue < 0) {
                    return Promise.reject(
                      new Error(
                        t(
                          'plants.form.financialMetrics.validation.valueBetween0And100'
                        )
                      )
                    );
                  } else if (minValue > 100) {
                    form?.setFieldsValue({ investmentLag: 100 });
                    return Promise.resolve();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="number"
              disabled={disabled}
              placeholder={t(
                'plants.form.financialMetrics.investmentLag.placeholder'
              )}
              addonAfter={t('plants.form.common.year')}
            />
          </Form.Item>
          {yearCount > 0 && (
            <Col className="mb-24">
              <Divider
                dashed
                style={{
                  marginBottom: '-12px',
                  borderColor: theme.colors.grayLightAccent,
                }}
                orientation="left"
              >
                <Typography.Text>
                  {t('plants.form.financialMetrics.capexDistribution.title')}
                </Typography.Text>
              </Divider>
              <Row className="preop-capex">
                <Form.List
                  name="preopCapexDistribution"
                  initialValue={Array(yearCount).fill({})}
                >
                  {(subFields) => {
                    const isOperationInitiation = 0;
                    const reversedKeys = subFields
                      .map((item) => item.key)
                      .reverse();

                    const updatedSubFields = subFields.map((item, index) => ({
                      ...item,
                      key: reversedKeys[index],
                    }));

                    return (
                      <Row gutter={[12, 12]}>
                        {updatedSubFields.map((subField) => (
                          <Col span={12}>
                            <Form.Item
                              key={`preOperationYear${subField.key}`}
                              name={subField.name}
                              label={
                                <Flex gap={8} align="center">
                                  <Typography.Text className="fs-14-regular text-black-color">
                                    {subField.key === isOperationInitiation
                                      ? t(
                                          'plants.form.financialMetrics.capexDistribution.operationInitiationYear.label'
                                        )
                                      : t(
                                          'plants.form.financialMetrics.capexDistribution.preOperationYear.label',
                                          { year: subField.key }
                                        )}
                                  </Typography.Text>
                                  <RTooltip
                                    title={
                                      subField.key === isOperationInitiation
                                        ? t(
                                            'plants.form.financialMetrics.capexDistribution.operationInitiationYear.tooltip.title'
                                          )
                                        : t(
                                            'plants.form.financialMetrics.capexDistribution.preOperationYear.tooltip.title',
                                            { year: subField.key }
                                          )
                                    }
                                    description={
                                      subField.key === isOperationInitiation
                                        ? t(
                                            'plants.form.financialMetrics.capexDistribution.operationInitiationYear.tooltip.description'
                                          )
                                        : t(
                                            'plants.form.financialMetrics.capexDistribution.preOperationYear.tooltip.description',
                                            { year: subField.key }
                                          )
                                    }
                                  >
                                    <IconInfo />
                                  </RTooltip>
                                </Flex>
                              }
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'plants.form.financialMetrics.capexDistribution.preOperationYear.requiredMessage',
                                    { year: subField.key }
                                  ),
                                },
                                {
                                  validator(_, value) {
                                    const minValue = parseFloat(value);
                                    if (isNaN(minValue) || minValue < 0) {
                                      return Promise.reject(
                                        new Error(
                                          t(
                                            'plants.form.financialMetrics.validation.valueBetween0And100'
                                          )
                                        )
                                      );
                                    } else if (minValue > 100) {
                                      return Promise.reject(
                                        new Error(
                                          t(
                                            'plants.form.financialMetrics.validation.valueBetween0And100'
                                          )
                                        )
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Input
                                disabled={disabled}
                                placeholder={t(
                                  'plants.form.financialMetrics.capexDistribution.preOperationYear.inputPlaceholder',
                                  { year: subField.key }
                                )}
                                addonAfter="%"
                              />
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>
                    );
                  }}
                </Form.List>
              </Row>
            </Col>
          )}
        </Col>
        <Col className="pl-8" span={12}>
          <Form.Item<PlantForm>
            name="lifespan"
            label={
              <Flex gap={8} align="center">
                <Typography.Text className="fs-14-regular text-black-color">
                  {t('plants.form.financialMetrics.lifespan.label')}
                </Typography.Text>
                <RTooltip
                  title={t(
                    'plants.form.financialMetrics.lifespan.tooltip.title'
                  )}
                  description={t(
                    'plants.form.financialMetrics.lifespan.tooltip.description'
                  )}
                >
                  <IconInfo />
                </RTooltip>
              </Flex>
            }
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.financialMetrics.lifespan.requiredMessage'
                ),
              },
            ]}
            normalize={normalizePositiveIntegerValuesInput}
          >
            <Input
              disabled={disabled}
              placeholder={t(
                'plants.form.financialMetrics.lifespan.placeholder'
              )}
              addonAfter={t('plants.form.common.year')}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col className="pr-8" span={12}>
          <Form.Item<PlantForm>
            name="depreciationYears"
            label={t('plants.form.financialMetrics.depreciationYears.label')}
            normalize={normalizePositiveIntegerValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.financialMetrics.depreciationYears.requiredMessage'
                ),
              },
            ]}
          >
            <Input
              disabled={disabled}
              placeholder={t(
                'plants.form.financialMetrics.depreciationYears.placeholder'
              )}
              addonAfter={t('plants.form.common.year')}
            />
          </Form.Item>
        </Col>
        <Col className="pl-8" span={12}>
          <Form.Item<PlantForm>
            name="loanFinancing"
            label={t('plants.form.financialMetrics.loanFinancing.label')}
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.financialMetrics.loanFinancing.requiredMessage'
                ),
              },
            ]}
          >
            <Radio.Group
              onChange={handleLoanFinancingChange}
              buttonStyle="solid"
              disabled={disabled}
            >
              <Radio.Button value={0}>
                {t('plants.form.financialMetrics.loanFinancing.fullEquity')}
              </Radio.Button>
              <Radio.Button value={1}>
                {t('plants.form.financialMetrics.loanFinancing.loanFinancing')}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          {loanFinancing === 1 && (
            <Row>
              <Col span={24}>
                <Form.Item<PlantForm>
                  name="loanFundedCapexRatio"
                  label={t(
                    'plants.form.financialMetrics.loanFundedCapexRatio.label'
                  )}
                  normalize={normalizePositiveNumericValuesInput}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'plants.form.financialMetrics.loanFundedCapexRatio.requiredMessage'
                      ),
                    },
                    {
                      validator(_, value) {
                        const minValue = parseFloat(value);
                        if (isNaN(minValue) || minValue < 0) {
                          return Promise.reject(
                            new Error(
                              t(
                                'plants.form.financialMetrics.validation.valueBetween0And100'
                              )
                            )
                          );
                        } else if (minValue > 100) {
                          form?.setFieldsValue({ loanFundedCapexRatio: 100 });
                          return Promise.resolve();
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'plants.form.financialMetrics.loanFundedCapexRatio.placeholder'
                    )}
                    addonAfter="%"
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item<PlantForm>
                  name="loanRepaymentPeriod"
                  label={t(
                    'plants.form.financialMetrics.loanRepaymentPeriod.label'
                  )}
                  normalize={normalizePositiveIntegerValuesInput}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'plants.form.financialMetrics.loanRepaymentPeriod.requiredMessage'
                      ),
                    },
                    {
                      validator(_, value) {
                        const minValue = parseFloat(value);
                        if (isNaN(minValue) || minValue < 1) {
                          return Promise.reject(
                            new Error(
                              t(
                                'plants.form.financialMetrics.validation.valueBetween1And100'
                              )
                            )
                          );
                        } else if (minValue > 100) {
                          form?.setFieldsValue({ loanRepaymentPeriod: 100 });
                          return Promise.resolve();
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'plants.form.financialMetrics.loanRepaymentPeriod.placeholder'
                    )}
                    onChange={handleLoanRepaymentPeriodChange}
                    addonAfter={t('plants.form.common.year')}
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              {loanRepaymentPeriod !== undefined &&
                loanRepaymentPeriod !== null &&
                loanRepaymentPeriod !== 0 && (
                  <Col span={24} className="mb-24">
                    <Divider
                      dashed
                      style={{
                        marginBottom: '-12px',
                        marginTop: '-12px',
                        marginLeft: '-6px',
                        borderColor: theme.colors.grayLightAccent,
                      }}
                      orientation="left"
                    >
                      <Typography.Text>
                        {t(
                          'plants.form.financialMetrics.annualInterestExpense.title'
                        )}
                      </Typography.Text>
                    </Divider>
                    <Row gutter={12} className="w-100 preop-capex">
                      {Array.from(
                        { length: loanRepaymentPeriod },
                        (_, index) => (
                          <Col span={12} key={index}>
                            <Form.Item<PlantForm>
                              name={['yearlyInterestExpense', index]}
                              label={t(
                                'plants.form.financialMetrics.annualInterestExpense.postOperationYear.label',
                                { year: index + 1 }
                              )}
                              normalize={normalizePositiveNumericValuesInput}
                              rules={[
                                {
                                  required: true,
                                  message: t(
                                    'plants.form.financialMetrics.annualInterestExpense.postOperationYear.requiredMessage',
                                    { year: index + 1 }
                                  ),
                                },
                              ]}
                            >
                              <Input
                                placeholder={t(
                                  'plants.form.financialMetrics.annualInterestExpense.postOperationYear.placeholder',
                                  { year: index + 1 }
                                )}
                                addonAfter={currencySymbol}
                                disabled={disabled}
                              />
                            </Form.Item>
                          </Col>
                        )
                      )}
                    </Row>
                  </Col>
                )}
            </Row>
          )}
        </Col>
      </Row>
      <Row>
        <Col className="pr-8" span={12}>
          <Form.Item<PlantForm>
            name="capexValue"
            label={t('plants.form.financialMetrics.capexValue.label')}
            normalize={normalizePositiveNumericValuesInput}
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.financialMetrics.capexValue.requiredMessage'
                ),
              },
            ]}
          >
            <Input
              disabled={disabled}
              placeholder={t(
                'plants.form.financialMetrics.capexValue.placeholder'
              )}
              addonAfter={selectAfter}
              onBlur={handleNumberBlur('capexValue')}
              onChange={handleNumberChange('capexValue')}
            />
          </Form.Item>
        </Col>
        <Col className="pl-8" span={12}>
          <Form.Item<PlantForm>
            name="fixedOpexValue"
            label={t('plants.form.financialMetrics.fixedOpexValue.label')}
            rules={[
              {
                required: true,
                message: t(
                  'plants.form.financialMetrics.fixedOpexValue.requiredMessage'
                ),
              },
            ]}
            normalize={normalizePositiveNumericValuesInput}
          >
            <Input
              disabled={disabled}
              placeholder={t(
                'plants.form.financialMetrics.fixedOpexValue.placeholder'
              )}
              addonAfter={currencySymbol}
              onBlur={handleNumberBlur('fixedOpexValue')}
              onChange={handleNumberChange('fixedOpexValue')}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form.Item>
  );
};

export default FinancialMetrics;
